import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Logger } from '../../utils/logger'

export interface ErrorBoundaryProps {
  children: ReactNode
  /** Optional fallback render function */
  fallback?: (error: Error) => ReactNode
  /** If true, renders nothing on error instead of unmounting the tree */
  suppressError?: boolean
  /** Identifier for the wrapped component, used in error logs */
  componentName?: string
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

/**
 * Error boundary component that catches rendering errors and logs them.
 * Wraps components to prevent a single error from crashing the entire UI.
 *
 * @example
 * <ErrorBoundary componentName="Navigation" suppressError>
 *   <DesktopNavigation />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const context = {
      componentName: this.props.componentName,
      errorMessage: error.message,
      errorStack: error.stack,
      componentStack: errorInfo.componentStack,
    }

    Logger.instance.error('Bookends Client Error', context)
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback && this.state.error) {
        return this.props.fallback(this.state.error)
      }
      if (this.props.suppressError) {
        return null
      }
      return null
    }

    return this.props.children
  }
}
